import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { FormField } from "@/components/ui/form-field";
import { TimeRangeRows } from "../components/TimeRangeRows";
import { UploadMultiImages } from "@/components/ui/upload-multi-images";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import { FoodDrinksActivity } from "../models/activity.model";
import { Link, MapPin, Clock, CalendarRange, FileText, Image as ImageIcon, UtensilsCrossed, Library, AlertTriangle } from "lucide-react";
import { DayRangeField } from "../components/DayRangeField";
import { useI18n } from "@/hooks/useI18n";
import ActivityModalShell from "./ActivityModalShell";
import type { DayAxis } from "../models/day-axis";
import ActivityDetailView from "./ActivityDetailView";
import { LibraryPicker } from "../components/LibraryPicker";
import type { LibraryActivity } from "@/lib/library/models/library-activity.model";

type FoodDrinksTab = 'details' | 'media' | 'documents' | 'library';

interface FoodDrinksModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<FoodDrinksActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<FoodDrinksActivity>;
  itineraryId: string;
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
  /** Asse dei giorni: sui template si sceglie "Giorno N" invece di una data. */
  axis?: DayAxis | null;
  defaultDate?: string;
  /** Nasconde il campo data: usato dalla Libreria, dove non c'è un giorno di riferimento. */
  hideDates?: boolean;
  /** Sola lettura: campi non interagibili, footer con solo "Chiudi" (usato dalla Libreria). */
  readOnly?: boolean;
}

const FoodDrinksModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryId, itineraryStartDate, itineraryEndDate, axis, defaultDate, hideDates = false, readOnly = false }: FoodDrinksModalProps) => {
  const { t } = useI18n();

  // Generate temporary UUID for new activities
  const [activityId] = useState<string>(() =>
    initialData?.id || crypto.randomUUID()
  );

  const [formData, setFormData] = useState<Partial<FoodDrinksActivity>>({
    id: activityId,
    title: '',
    description: '',
    link: '',
    address: '',
    media: [],
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, id: activityId });
    }
  }, [initialData, activityId]);

  const derivedMinDate: string | null =
    itineraryStartDate
    || (initialData as any)?.itineraryStartDate
    || (initialData as any)?.itinerary_start_date
    || (initialData as any)?.min_date
    || (initialData as any)?.minDate
    || null;

  const derivedMaxDate: string | null =
    itineraryEndDate
    || (initialData as any)?.itineraryEndDate
    || (initialData as any)?.itinerary_end_date
    || (initialData as any)?.max_date
    || (initialData as any)?.maxDate
    || null;

  const [errors, setErrors] = useState<Record<string, boolean>>({});
  // Import da libreria in due tempi: si sceglie, si conferma, poi si torna ai dati.
  const [pendingImport, setPendingImport] = useState<LibraryActivity | null>(null);
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [activeTab, setActiveTab] = useState<FoodDrinksTab>('details');

  useEffect(() => {
    if (!isOpen) {
      setShowValidationBanner(false);
      setErrors({});
      setActiveTab('details');
      setPendingImport(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !initialData && defaultDate) {
      setFormData(prev => ({ ...prev, start_date: defaultDate, end_date: defaultDate }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const validateForm = (): Record<string, boolean> => {
    const requiredFields = ['title', 'description'];
    const newErrors: Record<string, boolean> = {};

    requiredFields.forEach(field => {
      const value = formData[field as keyof FoodDrinksActivity];
      newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
    });

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (!Object.values(newErrors).some(error => error)) {
      onSave({
        ...formData,
        type: 'food-drinks' as const
      });
      onClose();
      return;
    }
    setShowValidationBanner(true);
    setActiveTab('details');
  };

  const hasDetailsError = ['title', 'description'].some(field => errors[field]);
  const requiredError = String(t('validation.fieldRequired'));

  // In modifica non si importa: il tab compare solo creando una nuova attività.
  const canImportFromLibrary = !initialData;

  const confirmImport = () => {
      if (!pendingImport) return;
      const imported = pendingImport.toItineraryActivity(formData.start_date || defaultDate);
      // L'id resta quello di questa modale: l'import precompila un inserimento nuovo,
      // non porta con sé l'identità dell'attività di libreria.
      setFormData({ ...(imported as Partial<FoodDrinksActivity>), id: activityId });
      setPendingImport(null);
      setErrors({});
      setShowValidationBanner(false);
      setActiveTab('details');
  };

  if (readOnly) {
    return (
      <ActivityDetailView
        isOpen={isOpen}
        onClose={onClose}
        icon={icon}
        title={t('builder.activities.foodDrinks')}
        activity={formData}
      />
    );
  }

  return (
    <ActivityModalShell
      isOpen={isOpen}
      onClose={onClose}
      icon={icon}
      title={t('builder.activities.foodDrinks')}
      formId="food-drinks-form"
      onSubmit={handleSubmit}
      showValidationBanner={showValidationBanner}
      activeTab={activeTab}
      onActiveTabChange={(value) => setActiveTab(value as FoodDrinksTab)}
      tabs={[
        {
          key: 'details',
          label: t('modals.tabDetails'),
          icon: <UtensilsCrossed className="h-3.5 w-3.5 shrink-0" />,
          hasError: hasDetailsError,
          content: (
            <div className="space-y-5">
              <FormField
                id="title"
                label={t('modals.title')}
                required
                error={errors.title ? requiredError : undefined}
              >
                <Input
                  id="title"
                  value={formData.title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={t('modals.restaurantName')}
                  className="h-9"
                  hasError={errors.title}
                />
              </FormField>

              <FormField
                id="address"
                label={t('modals.address')}
                icon={<MapPin className="h-3 w-3" />}
              >
                <Input
                  id="address"
                  value={formData.address || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder={t('modals.address')}
                  className="h-9"
                />
              </FormField>

              {/* Data e orario sulla stessa riga; su mobile vanno a capo. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {!hideDates && (
                <FormField
                  label={t('modals.dateRange')}
                  icon={<CalendarRange className="h-3 w-3" />}
                >
                  <DayRangeField
                    axis={axis}
                    value={formData}
                    onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                    hasError={false}
                    minDate={derivedMinDate || undefined}
                    maxDate={derivedMaxDate || undefined}
                    variant="rows"
                  />
                </FormField>
                )}

                <FormField label={t('modals.time')} icon={<Clock className="h-3 w-3" />}>
                  <TimeRangeRows
                    rows={[
                      { id: 'start_time', label: String(t('common.from')) },
                      { id: 'end_time', label: String(t('common.to')) },
                    ]}
                    values={formData}
                    onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                  />
                </FormField>
              </div>

              <FormField
                label={t('modals.description')}
                required
                error={errors.description ? requiredError : undefined}
              >
                <RichTextEditor
                  value={formData.description || ''}
                  onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                  placeholder={t('modals.describeRestaurant')}
                  hasError={errors.description}
                />
              </FormField>
            </div>
          ),
        },
        {
          key: 'media',
          optional: true,
          label: t('modals.tabMedia'),
          icon: <ImageIcon className="h-3.5 w-3.5 shrink-0" />,
          hasContent: (formData.media?.length || 0) > 0,
          content: (
            <FormField label={t('modals.media')} hint={t('modals.mediaHint')}>
              <UploadMultiImages
                images={formData.media || []}
                onImagesChange={(images) => setFormData(prev => ({ ...prev, media: images }))}
                label={t('modals.upload4Photos')}
                maxImages={10}
                itineraryId={itineraryId}
                activityId={activityId}
              />
            </FormField>
          ),
        },
        {
          key: 'documents',
          optional: true,
          label: t('modals.tabDocumentsAndLinks'),
          icon: <FileText className="h-3.5 w-3.5 shrink-0" />,
          hasContent: (formData.documentation?.length || 0) > 0 || !!formData.link,
          content: (
            <div className="space-y-5">
              <FormField label={t('modals.documentation')} hint={t('modals.documentationHint')}>
                <UploadMultiFiles
                  files={formData.documentation || []}
                  onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
                  label={t('modals.uploadBookingDocumentation')}
                  maxFiles={5}
                  itineraryId={itineraryId}
                  activityId={activityId}
                />
              </FormField>

              <FormField
                id="link"
                label={t('modals.link')}
                icon={<Link className="h-3 w-3" />}
                hint={t('modals.addSupportingLink')}
              >
                <Input
                  id="link"
                  type="url"
                  value={formData.link || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                  placeholder="https://..."
                  className="h-9"
                  hasError={errors.link}
                />
              </FormField>
            </div>
          ),
        },
        {
          key: 'library',
          group: 'import' as const,
          optional: true,
          hidden: !canImportFromLibrary,
          label: t('library.tabImport'),
          icon: <Library className="h-3.5 w-3.5 shrink-0" />,
          content: (
            <div className="space-y-4">
              {pendingImport && (
                <div className="flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                  <p className="flex items-center gap-2 text-xs font-medium text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {t('library.importConfirmTitle', { title: pendingImport.title })}
                  </p>
                  <p className="text-[11px] text-amber-700">{t('library.importConfirmBody')}</p>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={confirmImport}>
                      {t('library.importConfirmAction')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setPendingImport(null)}
                    >
                      {t('common.cancel')}
                    </Button>
                  </div>
                </div>
              )}

              <LibraryPicker
                activityType="food-drinks"
                enabled={isOpen && activeTab === 'library'}
                onSelect={setPendingImport}
                listClassName="max-h-[40vh]"
              />
            </div>
          ),
        },
      ]}
    />
  );
};

export default FoodDrinksModal;
